import { useEffect, useState } from "react";
import { Phone, MessageCircle, Instagram } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { SITE } from "@/data/site";

export default function FloatingButtons() {
  const wa = `https://wa.me/${SITE.phoneE164}?text=${encodeURIComponent(
    SITE.whatsappMessage
  )}`;
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    const footer = document.querySelector("[data-testid='site-footer']");
    if (!footer) return;
    const io = new IntersectionObserver(
      ([entry]) => setHidden(entry.isIntersecting),
      { rootMargin: "0px 0px -20% 0px", threshold: 0 }
    );
    io.observe(footer);
    return () => io.disconnect();
  }, []);

  const stack = [
    {
      key: "instagram",
      href: SITE.instagram,
      label: "Follow on Instagram",
      icon: <Instagram className="w-5 h-5" />,
      bg: "linear-gradient(135deg,#f09433,#e6683c 25%,#dc2743 50%,#cc2366 75%,#bc1888)",
      external: true,
      testId: "floating-instagram",
    },
    {
      key: "whatsapp",
      href: wa,
      label: "Chat on WhatsApp",
      icon: <MessageCircle className="w-5 h-5" />,
      bg: "#25D366",
      external: true,
      testId: "floating-whatsapp",
    },
    {
      key: "call",
      href: `tel:+${SITE.phoneE164}`,
      label: "Call Peak Adventures Malnad",
      icon: <Phone className="w-5 h-5" />,
      bg: "var(--pam-gold)",
      color: "var(--pam-forest)",
      external: false,
      testId: "floating-call",
    },
  ];

  return (
    <AnimatePresence>
      {!hidden && (
        <motion.div
          initial="hidden"
          animate="show"
          exit={{ opacity: 0, y: 20, transition: { duration: 0.3 } }}
          variants={{
            hidden: { opacity: 0, y: 20 },
            show: {
              opacity: 1,
              y: 0,
              transition: { staggerChildren: 0.12, delayChildren: 0.2 },
            },
          }}
          className="fixed z-[60] bottom-5 right-5 md:bottom-6 md:right-6 flex flex-col gap-3"
          data-testid="floating-buttons"
        >
          {stack.map((s) => (
            <motion.a
              key={s.key}
              href={s.href}
              {...(s.external
                ? { target: "_blank", rel: "noopener noreferrer" }
                : {})}
              aria-label={s.label}
              variants={{
                hidden: { opacity: 0, y: 20, scale: 0.8 },
                show: {
                  opacity: 1,
                  y: 0,
                  scale: 1,
                  transition: { type: "spring", stiffness: 300, damping: 20 },
                },
              }}
              animate={{
                scale: [1, 1.05, 1],
                transition: {
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut",
                },
              }}
              whileHover={{ scale: 1.15 }}
              whileTap={{ scale: 0.95 }}
              className="w-12 h-12 rounded-full grid place-items-center shadow-xl shadow-black/25 backdrop-blur-md ring-1 ring-white/20"
              style={{ background: s.bg, color: s.color || "#fff" }}
              data-testid={s.testId}
            >
              {s.icon}
            </motion.a>
          ))}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

